import React from 'react';
import {
    Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow,
} from '@mui/material';
import { AcceptanceBadge, fmt, fmtFrac } from './msaFormat';

// ---------------------------------------------------------------------------
// AttributeAnalyticResults — renders the signal-detection engine payload.
//
// results (all probabilities are FRACTIONS 0..1):
//   byOperator: { [op]: { detectionProbability|null, falseAlarmRate|null,
//                         missRate|null, escapeRate|null, effectiveness|null,
//                         counts:{tp,fn,fp,tn} } }
//   overall:    { detectionProbability|null, falseAlarmRate|null,
//                 effectiveness|null, counts:{tp,fn,fp,tn} }
//   logisticModel: null | { rocCurve:[{fpr,tpr}×3], auc /*0..1*/, ... }
//   riskAnalysis: { expectedEscapeRate|null, expectedMissRate|null, recommendation }
//
// Acceptance is derived HERE from overall.effectiveness.
// ---------------------------------------------------------------------------

function effectivenessStatus(eff) {
    if (eff === null || eff === undefined || !isFinite(eff)) return undefined;
    if (eff >= 0.9) return 'acceptable';
    if (eff >= 0.8) return 'marginal';
    return 'unacceptable';
}

const panelSx = {
    p: 2,
    backgroundColor: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 1,
};

function MetricCard({ label, value, accent = '#90caf9' }) {
    return (
        <Paper
            elevation={0}
            sx={{
                ...panelSx,
                flex: '1 1 140px',
                minWidth: 130,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
            }}
        >
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.68rem' }}>
                {label}
            </Typography>
            <Typography sx={{ color: accent, fontWeight: 700, fontSize: '1.4rem', lineHeight: 1.1 }}>
                {value}
            </Typography>
        </Paper>
    );
}

const sectionTitleSx = {
    color: 'rgba(255,255,255,0.9)',
    fontWeight: 600,
    fontSize: '0.9rem',
    mb: 1,
};

const headCellSx = {
    color: 'rgba(255,255,255,0.55)',
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    borderBottom: '1px solid rgba(255,255,255,0.12)',
    backgroundColor: '#1a1f26',
    py: 1,
    px: 1.25,
    whiteSpace: 'nowrap',
};

const bodyCellSx = {
    color: 'rgba(255,255,255,0.85)',
    fontSize: '0.82rem',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    py: 0.75,
    px: 1.25,
    whiteSpace: 'nowrap',
};

// --- Inline ROC plot -------------------------------------------------------
// Unit square, chance diagonal, the 3-vertex rocCurve polyline, and the
// operating point (overall FPR, TPR). viewBox keeps it responsive.
function RocPlot({ rocCurve, operatingPoint }) {
    const W = 180;
    const pad = 22;
    const span = W - pad * 2;
    // Data coords (0..1) → SVG coords. y is inverted (SVG y grows downward).
    const sx = (v) => pad + v * span;
    const sy = (v) => pad + (1 - v) * span;

    const curve = Array.isArray(rocCurve)
        ? rocCurve.filter((p) => p && isFinite(p.fpr) && isFinite(p.tpr))
        : [];
    const polyPoints = curve.map((p) => `${sx(p.fpr)},${sy(p.tpr)}`).join(' ');

    const opValid =
        operatingPoint && isFinite(operatingPoint.fpr) && isFinite(operatingPoint.tpr);

    return (
        <Box
            component="svg"
            viewBox={`0 0 ${W} ${W}`}
            role="img"
            aria-label="ROC curve"
            sx={{ width: '100%', maxWidth: 200, height: 'auto', display: 'block' }}
        >
            {/* plot frame */}
            <rect x={pad} y={pad} width={span} height={span} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
            {/* chance diagonal */}
            <line x1={sx(0)} y1={sy(0)} x2={sx(1)} y2={sy(1)} stroke="rgba(255,255,255,0.25)" strokeWidth="1" strokeDasharray="3 3" />
            {/* ROC polyline */}
            {curve.length >= 2 && (
                <polyline points={polyPoints} fill="none" stroke="#90caf9" strokeWidth="1.6" strokeLinejoin="round" />
            )}
            {/* operating point */}
            {opValid && (
                <circle cx={sx(operatingPoint.fpr)} cy={sy(operatingPoint.tpr)} r="3.4" fill="#81c784" stroke="#0b0f14" strokeWidth="1" />
            )}
            {/* axis labels */}
            <text x={pad + span / 2} y={W - 4} fill="rgba(255,255,255,0.5)" fontSize="8" textAnchor="middle">FPR</text>
            <text x={7} y={pad + span / 2} fill="rgba(255,255,255,0.5)" fontSize="8" textAnchor="middle" transform={`rotate(-90 7 ${pad + span / 2})`}>TPR</text>
        </Box>
    );
}

function CountCell({ counts }) {
    const c = counts || {};
    const g = (k) => (Number.isFinite(c[k]) ? c[k] : '—');
    return (
        <span style={{ color: 'rgba(255,255,255,0.6)', fontVariantNumeric: 'tabular-nums' }}>
            {g('tp')} / {g('fn')} / {g('fp')} / {g('tn')}
        </span>
    );
}

function AttributeAnalyticResults({ results }) {
    if (!results) return null;

    const overall = results.overall || {};
    const byOperator = results.byOperator || {};
    const logistic = results.logisticModel || null;
    const risk = results.riskAnalysis || {};

    const status = effectivenessStatus(overall.effectiveness);
    const auc = logistic && isFinite(logistic.auc) ? logistic.auc : null;
    const operators = Object.keys(byOperator);

    const operatingPoint = {
        fpr: overall.falseAlarmRate,
        tpr: overall.detectionProbability,
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Acceptance verdict */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <AcceptanceBadge status={status} label="Effectiveness" />
            </Box>

            {/* Overall metric cards */}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <MetricCard label="Effectiveness" value={fmtFrac(overall.effectiveness)} accent="#81c784" />
                <MetricCard label="Prob. of Detection" value={fmtFrac(overall.detectionProbability)} />
                <MetricCard label="False-Alarm Rate" value={fmtFrac(overall.falseAlarmRate)} accent="#ffb74d" />
                {logistic && <MetricCard label="AUC" value={fmt(auc, 3)} />}
            </Box>

            {/* ROC / operating point */}
            <Box>
                <Typography sx={sectionTitleSx}>Signal Detection (ROC)</Typography>
                <Box sx={{ ...panelSx, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    {logistic && Array.isArray(logistic.rocCurve) ? (
                        <RocPlot rocCurve={logistic.rocCurve} operatingPoint={operatingPoint} />
                    ) : null}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 160 }}>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                            Operating point
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.82rem' }}>
                            FPR {fmtFrac(overall.falseAlarmRate)} · TPR {fmtFrac(overall.detectionProbability)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.82rem' }}>
                            AUC {auc === null ? '—' : fmt(auc, 3)}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Per-operator table */}
            <Box>
                <Typography sx={sectionTitleSx}>Per-Operator Detection</Typography>
                <TableContainer sx={{ border: '1px solid rgba(255,255,255,0.08)', borderRadius: 1, overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={headCellSx}>Operator</TableCell>
                                <TableCell sx={headCellSx} align="right">POD</TableCell>
                                <TableCell sx={headCellSx} align="right">False Alarm</TableCell>
                                <TableCell sx={headCellSx} align="right">Miss</TableCell>
                                <TableCell sx={headCellSx} align="right">Effectiveness</TableCell>
                                <TableCell sx={headCellSx} align="right" title="True Positive / False Negative / False Positive / True Negative">
                                    TP / FN / FP / TN
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {operators.length === 0 ? (
                                <TableRow>
                                    <TableCell sx={{ ...bodyCellSx, color: 'rgba(255,255,255,0.4)' }} colSpan={6}>
                                        No per-operator data.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                operators.map((op) => {
                                    const m = byOperator[op] || {};
                                    return (
                                        <TableRow key={op} hover sx={{ '&:hover': { backgroundColor: 'rgba(144,202,249,0.04)' } }}>
                                            <TableCell sx={{ ...bodyCellSx, color: '#90caf9', fontWeight: 600 }}>{op}</TableCell>
                                            <TableCell sx={bodyCellSx} align="right">{fmtFrac(m.detectionProbability)}</TableCell>
                                            <TableCell sx={bodyCellSx} align="right">{fmtFrac(m.falseAlarmRate)}</TableCell>
                                            <TableCell sx={bodyCellSx} align="right">{fmtFrac(m.missRate)}</TableCell>
                                            <TableCell sx={bodyCellSx} align="right">{fmtFrac(m.effectiveness)}</TableCell>
                                            <TableCell sx={bodyCellSx} align="right"><CountCell counts={m.counts} /></TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Risk block */}
            <Box>
                <Typography sx={sectionTitleSx}>Risk Analysis</Typography>
                <Box sx={panelSx}>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: risk.recommendation ? 1.5 : 0 }}>
                        <Box>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block' }}>
                                Expected Miss Rate
                            </Typography>
                            <Typography sx={{ color: '#ffb74d', fontWeight: 600, fontSize: '1.1rem' }}>
                                {fmtFrac(risk.expectedMissRate)}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block' }}>
                                Expected Escape Rate
                            </Typography>
                            <Typography sx={{ color: '#e57373', fontWeight: 600, fontSize: '1.1rem' }}>
                                {fmtFrac(risk.expectedEscapeRate)}
                            </Typography>
                        </Box>
                    </Box>
                    {risk.recommendation && (
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.5 }}>
                            {risk.recommendation}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
}

export default AttributeAnalyticResults;
